import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  clearPendingInviteRef,
  getPendingInviteRef,
  persistPendingInviteRef,
} from '../../services/referralInviteService';
import { sanitizeReferralCode } from '../../utils/referralAttribution';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { getCurrentMillis } from '../../utils/dateUtils';
import { subscriptionService } from '../../services/subscriptionService';
import { getFreePlan } from '../../constants/subscriptionPlans';
import { SUBSCRIPTION_PLAN_IDS, Subscription } from '../../types/subscription';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import { 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Eye, 
  EyeOff, 
  X, 
  AlertCircle,
  CheckCircle,
  Camera,
  Calendar,
  Users,
  Gift,
  Heart,
  CreditCard,
  MapPin
} from 'lucide-react';
import type { User as UserType } from '../../types/user';
import SignupStepBadge from '../../components/auth/SignupStepBadge';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, setLanguage, strings, isRTL } = useLocalization();
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);


  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    gender: '',
    referenceCode: '',
    maritalStatus: '',
    nationalID: '',
    phoneNumber2: '',
    address: '',
    city: 'Cairo',
    titleID: '1'
  });

  // OW-513 / RF-006 — prefill from ?ref= or pending invite localStorage
  useEffect(() => {
    const fromUrl = searchParams.get('ref')?.trim();
    if (fromUrl) persistPendingInviteRef(fromUrl);
    const pending = fromUrl || getPendingInviteRef() || '';
    if (!pending) return;
    setFormData((prev) =>
      prev.referenceCode ? prev : { ...prev, referenceCode: pending },
    );
  }, [searchParams]);

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    const password = formData.password;
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    score = Object.values(checks).filter(Boolean).length;

    if (score <= 2) return { score, label: language === 'ar' ? 'ضعيف' : 'Weak', color: 'text-red-500' };
    if (score <= 3) return { score, label: language === 'ar' ? 'متوسط' : 'Fair', color: 'text-orange-500' };
    if (score <= 4) return { score, label: language === 'ar' ? 'جيد' : 'Good', color: 'text-yellow-500' };
    return { score, label: language === 'ar' ? 'قوي' : 'Strong', color: 'text-green-500' };
  }, [formData.password, language]);

  // Synchronous validation helper (returns error message or null) - FIXES RACE CONDITION
  // Accepts full formData for cross-field validation (e.g., confirmPassword needs password)
  const validateFieldSync = React.useCallback((name: string, value: string, currentFormData?: typeof formData): string | null => {
    const formDataToUse = currentFormData || formData;
    const trimmedValue = (value || '').trim();
    
    switch (name) {
      case 'firstName':
        if (!trimmedValue) {
          return language === 'ar' ? 'الاسم الأول مطلوب' : 'First name is required';
        } else if (trimmedValue.length < 2) {
          return language === 'ar' ? 'الاسم الأول يجب أن يكون حرفين على الأقل' : 'First name must be at least 2 characters';
        }
        break;
        
      case 'lastName':
        if (!trimmedValue) {
          return language === 'ar' ? 'الاسم الأخير مطلوب' : 'Last name is required';
        } else if (trimmedValue.length < 2) {
          return language === 'ar' ? 'الاسم الأخير يجب أن يكون حرفين على الأقل' : 'Last name must be at least 2 characters';
        }
        break;
        
      case 'middleName':
        // Middle name is optional, but if provided, should be at least 2 characters
        if (trimmedValue && trimmedValue.length < 2) {
          return language === 'ar' ? 'الاسم الأوسط يجب أن يكون حرفين على الأقل' : 'Middle name must be at least 2 characters';
        }
        break;
        
      case 'email':
        if (!trimmedValue) {
          return language === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
          return language === 'ar' ? 'البريد الإلكتروني غير صحيح' : 'Please enter a valid email address';
        }
        break;
        
      case 'phoneNumber':
        if (!trimmedValue) {
          return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
        } else if (!/^[+]?[0-9\s\-()]{10,}$/.test(trimmedValue)) {
          return language === 'ar' ? 'رقم الهاتف غير صحيح' : 'Please enter a valid phone number';
        }
        break;
        
      case 'password':
        if (!value) {
          return language === 'ar' ? 'كلمة المرور مطلوبة' : 'Password is required';
        } else if (value.length < 8) {
          return language === 'ar' ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters';
        }
        break;
        
      case 'confirmPassword':
        if (!value) {
          return language === 'ar' ? 'تأكيد كلمة المرور مطلوب' : 'Password confirmation is required';
        } else if (value !== formDataToUse.password) {
          return language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match';
        }
        break;
        
      case 'birthDate':
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          
          if (birthDate > today) {
            return language === 'ar' ? 'تاريخ الميلاد لا يمكن أن يكون في المستقبل' : 'Birth date cannot be in the future';
          } else if (age < 18) {
            return language === 'ar' ? 'يجب أن تكون 18 سنة على الأقل' : 'You must be at least 18 years old';
          } else if (age > 120) {
            return language === 'ar' ? 'تاريخ الميلاد غير صحيح' : 'Invalid birth date';
          }
        }
        break;
        
      case 'gender':
      case 'maritalStatus':
      case 'address':
      case 'city':
      case 'titleID':
        // Optional fields, no validation needed
        break;
        
      case 'referenceCode':
        if (trimmedValue && trimmedValue.length < 3) {
          return language === 'ar' ? 'كود الإحالة يجب أن يكون 3 أحرف على الأقل' : 'Reference code must be at least 3 characters';
        }
        break;
        
      case 'nationalID':
        if (trimmedValue && trimmedValue.length < 10) {
          return language === 'ar' ? 'الرقم القومي يجب أن يكون 10 أرقام على الأقل' : 'National ID must be at least 10 digits';
        }
        break;
        
      case 'phoneNumber2':
        if (trimmedValue && !/^[+]?[0-9\s\-()]{10,}$/.test(trimmedValue)) {
          return language === 'ar' ? 'رقم الهاتف الثاني غير صحيح' : 'Please enter a valid second phone number';
        }
        break;
    }
    
    return null; // No error
  }, [formData, language]);

  // Compute form validity for button disabled state (memoized to avoid re-validation on every render)
  const isFormValid = useMemo(() => {
    try {
      // Check required fields
      const requiredFields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword'];
      for (const key of requiredFields) {
        const fieldValue = String(formData[key as keyof typeof formData] || '');
        const error = validateFieldSync(key, fieldValue, formData);
        if (error) {
          return false;
        }
      }
      
      // Check password match and length
      if (formData.password !== formData.confirmPassword) {
        return false;
      }
      
      // Password length check - only if password is not empty (empty is caught by required check)
      if (formData.password && formData.password.length < 8) {
        return false;
      }
      
      return true;
    } catch (err) {
      return false; // Default to invalid if there's an error
    }
  }, [formData, validateFieldSync]);

  // Handle profile image upload and convert to Base64
  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(language === 'ar' ? 'يرجى اختيار ملف صورة صالح' : 'Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' : 'Image size must be less than 5MB');
        return;
      }
      
      // Create preview and convert to Base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setProfileImagePreview(base64String);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous errors
      if (error) setError(null);
    }
  };

  const removeProfileImage = () => {
    setProfileImagePreview(null);
  };

  // Lawyer titles data
  const lawyerTitles = [
    { id: "1", nameEn: "Lawyer", nameAr: "محام" },
    { id: "2", nameEn: "Legal Advisor", nameAr: "مستشار" },
    { id: "3", nameEn: "Legal Assistant", nameAr: "مساعد محام" },
    { id: "4", nameEn: "Corporate Legal Officer", nameAr: "مسؤول قانوني في شركة" },
    { id: "5", nameEn: "Judicial Collector", nameAr: "مسؤول تحصيل قضائي" },
    { id: "6", nameEn: "Compliance Officer", nameAr: "مسؤول التزام قانوني" },
    { id: "7", nameEn: "Legal Researcher", nameAr: "باحث قانوني" },
    { id: "8", nameEn: "Law Student", nameAr: "طالب حقوق" },
    { id: "9", nameEn: "Paralegal", nameAr: "مساعد قانوني محترف" },
    { id: "10", nameEn: "Forensic Accountant", nameAr: "محاسب قانوني" },
    { id: "11", nameEn: "Legal Secretary", nameAr: "سكرتير قانوني" },
    { id: "12", nameEn: "Trainee", nameAr: "متدرب" },
    { id: "13", nameEn: "Other", nameAr: "أخرى" }
  ];

  // Collect device information
  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    let nameOS = 'Unknown';
    let versionOS = 'Unknown';
    let versionName = 'Unknown';

    // Detect OS
    if (userAgent.indexOf('Win') !== -1) {
      nameOS = 'Windows';
      const match = userAgent.match(/Windows NT (\d+\.\d+)/);
      if (match) versionOS = match[1];
    } else if (userAgent.indexOf('Mac') !== -1) {
      nameOS = 'Mac';
      const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
      if (match) versionOS = match[1].replace('_', '.');
    } else if (userAgent.indexOf('Linux') !== -1) {
      nameOS = 'Linux';
    } else if (userAgent.indexOf('Android') !== -1) {
      nameOS = 'Android';
      const match = userAgent.match(/Android (\d+\.\d+)/);
      if (match) versionOS = match[1];
    } else if (userAgent.indexOf('iPhone') !== -1 || userAgent.indexOf('iPad') !== -1) {
      nameOS = 'iOS';
      const match = userAgent.match(/OS (\d+[._]\d+)/);
      if (match) versionOS = match[1].replace('_', '.');
    }

    // Detect browser
    if (userAgent.indexOf('Chrome') !== -1) {
      versionName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
      if (match) versionOS = match[1];
    } else if (userAgent.indexOf('Firefox') !== -1) {
      versionName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
      if (match) versionOS = match[1];
    } else if (userAgent.indexOf('Safari') !== -1) {
      versionName = 'Safari';
      const match = userAgent.match(/Version\/(\d+\.\d+)/);
      if (match) versionOS = match[1];
    } else if (userAgent.indexOf('Edge') !== -1) {
      versionName = 'Edge';
      const match = userAgent.match(/Edge\/(\d+\.\d+)/);
      if (match) versionOS = match[1];
    }

    // Generate device ID (using a combination of screen resolution, timezone, and language)
    const deviceID = `${window.screen.width}x${window.screen.height}-${Intl.DateTimeFormat().resolvedOptions().timeZone}-${navigator.language}-${Date.now().toString(36)}`;

    // Generate device name
    const deviceName = `${nameOS} ${versionOS} - ${versionName}`;

    return {
      deviceID,
      deviceName,
      nameOS,
      versionOS,
      versionCode: '0.0.1',
      versionName
    };
  };

  // Real-time validation (updates state for UI)
  const validateField = (name: string, value: string) => {
    const errors = { ...fieldErrors };
    const error = validateFieldSync(name, value);
    
    if (error) {
      errors[name] = error;
    } else {
      delete errors[name];
    }
    
    setFieldErrors(errors);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear general error when user starts typing in email field (most common error source)
    if (error && name === 'email') setError(null);
    
    // Validate field in real-time
    validateField(name, value);
  };

  const validateForm = () => {
    // FIX: Use synchronous validation to avoid race condition
    // Validate all required fields synchronously
    const newErrors: {[key: string]: string} = {};
    
    // Validate required fields (pass formData for cross-field validation)
    const requiredFields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword'];
    requiredFields.forEach(key => {
      const fieldValue = formData[key as keyof typeof formData];
      const error = validateFieldSync(key, String(fieldValue || ''), formData);
      if (error) {
        newErrors[key] = error;
      }
    });
    
    // Validate optional fields if they have values
    const optionalFields = ['middleName', 'birthDate', 'referenceCode', 'nationalID', 'phoneNumber2'];
    optionalFields.forEach(key => {
      const value = formData[key as keyof typeof formData];
      if (value) {
        const error = validateFieldSync(key, value, formData);
        if (error) {
          newErrors[key] = error;
        }
      }
    });
    
    // Update state with all errors (for UI display)
    setFieldErrors(newErrors);
    
    // Check if terms are accepted
    if (!acceptedTerms) {
      setError(language === 'ar' ? 'يجب الموافقة على الشروط والأحكام' : 'You must accept the terms and conditions');
      return false;
    }
    
    // Check if there are any field errors (using synchronously computed errors)
    if (Object.keys(newErrors).length > 0) {
      setError(language === 'ar' ? 'يرجى تصحيح الأخطاء في النموذج' : 'Please correct the errors in the form');
      return false;
    }
    
    // Additional validation (double-check password match and length)
    if (formData.password !== formData.confirmPassword) {
      setError(language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return false;
    }
    
    if (formData.password.length < 8) {
      setError(language === 'ar' ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Double-check button disabled conditions
    if (loading) {
      return;
    }
    
    if (!acceptedTerms) {
      setError(language === 'ar' ? 'يجب الموافقة على الشروط والأحكام' : 'You must accept the terms and conditions');
      return;
    }
    
    if (!isFormValid) {
      // Still run validateForm to show errors to user
      validateForm();
      return;
    }
    
    const isValid = validateForm();
    
    if (!isValid) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;

      // Use Base64 string for profile image if provided (photo moved to separate step)
      const photoURL = '';

      // Update Firebase Auth profile
      await updateProfile(user, {
        displayName: `${formData.firstName} ${formData.lastName}`,
        photoURL: ''
      });

              // Get device information
              const deviceInfo = getDeviceInfo();

              // Create user document without subscription first
              // Subscription will be created after user chooses trial or free plan

              // Create user document in Firestore
              const userData: Omit<UserType, 'id'> & { skippedTrialOffer?: boolean } = {
                firstName: formData.firstName,
                middleName: formData.middleName || '',
                lastName: formData.lastName,
                email: formData.email,
                phoneNumber: '',
                role: 'lawyer', // Default role for new users
                isActive: true,
                createdAt: getCurrentMillis(),
                updatedAt: getCurrentMillis(),
                // Optional fields with user input
                phoneNumber2: '',
                nationalID: formData.nationalID || '',
                birthdate: '',
                gender: '',
                martialStatus: formData.maritalStatus || '',
                address: formData.address || '',
                city: formData.city || '',
                country: 'Egypt', // Always Egypt
                photoURL: '',
                referenceCode: sanitizeReferralCode(formData.referenceCode, user.uid),
                titleID: formData.titleID || '1',
                managersIDs: [],
                officesIDsList: [],
                clientsReferences: [],
                teamMembersReferences: [],
                isLawyer: true,
                isAccountOwner: true,
                fcmToken: '',
                deviceInfo: deviceInfo,
                isTermsConditionsAccepted: true, // User accepted terms during signup
                subscription: null, // Will be created after user chooses trial or free plan
                skippedTrialOffer: false, // Will be updated based on user choice
                isArchived: false,
                isDeleted: false,
                // Computed properties for display
                name: `${formData.firstName} ${formData.middleName ? formData.middleName + ' ' : ''}${formData.lastName}`
              };

      // Step 3: Create user document in Firestore
      try {
        await setDoc(doc(db, 'Users', user.uid), userData);
        clearPendingInviteRef();
      } catch (firestoreError: any) {
        // FIX: Rollback - delete Firebase Auth user if Firestore write fails
        try {
          await user.delete();
        } catch (deleteError) {
          console.error('Failed to delete user after Firestore error:', deleteError);
        }
        throw new Error(language === 'ar' ? 'فشل حفظ البيانات. يرجى المحاولة مرة أخرى.' : 'Failed to save user data. Please try again.');
      }
      // Step 4: Create subscription document in Firestore
      // Create a professional trial subscription by default
      const now = Date.now();
      const registrationDate = new Date();
      
      // Calculate end date based on registration date
      // Import trial extension utils to get the correct end date
      const { TrialExtensionUtils } = await import('../../constants/trialExtension');
      const trialEndDate = TrialExtensionUtils.getTrialEndDate(registrationDate);
      const shouldGetExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(registrationDate);
      
      // Create subscription data (without id, createdAt, updatedAt - service will add them)
      const trialSubscriptionData = {
        userId: user.uid,
        userName: userData.name,
        userPhone: userData.phoneNumber,
        planId: 'professional', // Professional plan for trial
        status: 'trial' as const, // Trial status
        startDate: now.toString(),
        endDate: trialEndDate.getTime().toString(),
        totalPrice: 0, // Trial is free
        currency: 'EGP' as const,
        billingCycle: 'monthly' as const,
        isActive: true,
        // Extended trial metadata
        trialExtensionReason: shouldGetExtendedTrial ? 'business_extension' as const : 'standard_trial' as const,
        createdBy: user.uid,
        updatedBy: user.uid
      } as Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'>;

      // Create subscription document in Firestore
      // Initialize updatedUserData with base userData
      let updatedUserData = { ...userData };
      
      try {
        const createdSubscription = await subscriptionService.createSubscription(trialSubscriptionData);

        // Update user data with subscription
        updatedUserData = {
          ...userData,
          subscription: createdSubscription
        };

        // Update user document with subscription info
        await setDoc(doc(db, 'Users', user.uid), updatedUserData);
      } catch (subscriptionError: any) {
        // FIX: Subscription creation failed - user and user doc exist, but no subscription
        // Log error but don't fail completely - user can still login and subscription can be created later
        console.error('Subscription creation failed, but user account created:', subscriptionError);
        // Continue with user creation - subscription can be fixed later
        // updatedUserData remains without subscription
      }

      // Step 5: demographics then photo (OW-185 / OW-183 parity)
      const { markNewSignupFlow } = await import('../../utils/googleAuth');
      markNewSignupFlow(true);
      navigate('/auth/demographics', { replace: true });
    } catch (error: any) {
      // Handle Firebase Auth errors
      if (error?.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            setError(strings.signup.emailInUse);
            break;
          case 'auth/invalid-email':
            setError(strings.signup.invalidEmail);
            break;
          case 'auth/weak-password':
            setError(strings.signup.weakPassword);
            break;
          default:
            setError(error.message || strings.common.error);
        }
      } else {
        // Handle non-Firebase errors
        const errorMessage = error?.message || error?.toString() || (language === 'ar' ? 'حدث خطأ غير متوقع' : 'An unexpected error occurred');
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      
      {/* Header */}
      <div className="bg-primary dark:bg-primary-dark shadow-sm border-b border-primary/20 dark:border-primary-light/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <MorafaaLogo size="sm" variant="light" />
              <span className="text-xl font-bold text-white">
                {isRTL ? 'مرافعة' : 'Morafaa'}
              </span>
            </div>
            
            {/* Home Button, Theme & Language Toggle */}
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <Link
                to="/"
                className="px-3 py-1 text-sm font-medium text-white/80 hover:text-white transition-colors duration-200"
              >
                {isRTL ? 'الرئيسية' : 'Home'}
              </Link>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
                title={language === 'ar' ? 'تبديل المظهر' : 'Toggle Theme'}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              
              <button
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className="px-3 py-1 text-sm rounded-md transition-colors bg-white/20 text-white hover:bg-white/30"
              >
                {language === 'ar' ? 'English' : 'العربية'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 min-h-screen">
      <div className="w-full max-w-md">

        {/* Sign Up Form */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700">
          {/* Header — identity only; phone/birth/gender/photo → later steps (OW-181 / OW-185) */}
          <div className="text-center mb-8">
            <SignupStepBadge step={1} isRTL={isRTL} />
            <h1 className="m3-headline-large text-slate-800 dark:text-white mb-2 tracking-tight">
              {strings.signup.welcome}
            </h1>
            <p className="m3-body-large text-slate-600 dark:text-slate-300">
              {strings.signup.subtitle}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm font-medium">
                {error}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                <User className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                <span className="flex items-center">
                  {strings.signup.firstName}
                  <span className="text-red-500 ml-1 rtl:ml-0 rtl:mr-1">*</span>
                </span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                dir={isRTL ? 'rtl' : 'ltr'}
                className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                  fieldErrors.firstName 
                    ? 'border-red-500 focus:border-red-500' 
                    : formData.firstName && !fieldErrors.firstName 
                      ? 'border-green-500 focus:border-green-500' 
                      : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                }`}
                placeholder={strings.signup.firstNamePlaceholder}
                required
              />
              {fieldErrors.firstName && (
                <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {fieldErrors.firstName}
                </div>
              )}
            </div>

            {/* Middle Name */}
            <div>
              <label htmlFor="middleName" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                <User className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                <span className="flex items-center">
                  {language === 'ar' ? 'الاسم الأوسط' : 'Middle Name'}
                  <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                </span>
              </label>
              <input
                type="text"
                id="middleName"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
                dir={isRTL ? 'rtl' : 'ltr'}
                className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                  fieldErrors.middleName 
                    ? 'border-red-500 focus:border-red-500' 
                    : formData.middleName && !fieldErrors.middleName 
                      ? 'border-green-500 focus:border-green-500' 
                      : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                }`}
                placeholder={language === 'ar' ? 'أدخل الاسم الأوسط' : 'Enter middle name'}
              />
              {fieldErrors.middleName && (
                <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {fieldErrors.middleName}
                </div>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                <User className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                <span className="flex items-center">
                  {strings.signup.lastName}
                  <span className="text-red-500 ml-1 rtl:ml-0 rtl:mr-1">*</span>
                </span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                dir={isRTL ? 'rtl' : 'ltr'}
                className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                  fieldErrors.lastName 
                    ? 'border-red-500 focus:border-red-500' 
                    : formData.lastName && !fieldErrors.lastName 
                      ? 'border-green-500 focus:border-green-500' 
                      : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                }`}
                placeholder={strings.signup.lastNamePlaceholder}
                required
              />
              {fieldErrors.lastName && (
                <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {fieldErrors.lastName}
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                <Mail className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                <span className="flex items-center">
                  {strings.signup.email}
                  <span className="text-red-500 ml-1 rtl:ml-0 rtl:mr-1">*</span>
                </span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                dir={isRTL ? 'rtl' : 'ltr'}
                className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                  fieldErrors.email 
                    ? 'border-red-500 focus:border-red-500' 
                    : formData.email && !fieldErrors.email 
                      ? 'border-green-500 focus:border-green-500' 
                      : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                }`}
                placeholder={strings.signup.emailPlaceholder}
                required
              />
              {fieldErrors.email && (
                <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {fieldErrors.email}
                </div>
              )}
            </div>

            {/* Phone Number — moved to DemographicsSetup (OW-185) */}
            {false && (
            <div>
              <label htmlFor="phoneNumber" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                <Phone className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                <span className="flex items-center">
                  {strings.signup.phone}
                  <span className="text-red-500 ml-1 rtl:ml-0 rtl:mr-1">*</span>
                </span>
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                dir={isRTL ? 'rtl' : 'ltr'}
                className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                  fieldErrors.phoneNumber 
                    ? 'border-red-500 focus:border-red-500' 
                    : formData.phoneNumber && !fieldErrors.phoneNumber 
                      ? 'border-green-500 focus:border-green-500' 
                      : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                }`}
                placeholder={strings.signup.phonePlaceholder}
              />
              {fieldErrors.phoneNumber && (
                <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {fieldErrors.phoneNumber}
                </div>
              )}
            </div>
            )}

            {/* Password */}
            <div>
              <label htmlFor="password" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                <Lock className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                <span className="flex items-center">
                  {strings.signup.password}
                  <span className="text-red-500 ml-1 rtl:ml-0 rtl:mr-1">*</span>
                </span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                    isRTL ? 'pl-12 pr-4' : 'pr-12 pl-4'
                  } ${
                    fieldErrors.password 
                      ? 'border-red-500 focus:border-red-500' 
                      : formData.password && !fieldErrors.password 
                        ? 'border-green-500 focus:border-green-500' 
                        : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                  }`}
                  placeholder={strings.signup.passwordPlaceholder}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-200 ${
                    isRTL ? 'right-3' : 'right-3'
                  }`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {language === 'ar' ? 'قوة كلمة المرور:' : 'Password Strength:'}
                    </span>
                    <span className={`text-sm font-medium ${passwordStrength.color}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        passwordStrength.score <= 2 ? 'bg-red-500' :
                        passwordStrength.score <= 3 ? 'bg-orange-500' :
                        passwordStrength.score <= 4 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                  
                  {/* Password Requirements */}
                  <div className="mt-3 space-y-1">
                    {[
                      { check: formData.password.length >= 8, text: language === 'ar' ? '8 أحرف على الأقل' : 'At least 8 characters' },
                      { check: /[a-z]/.test(formData.password), text: language === 'ar' ? 'حرف صغير' : 'Lowercase letter' },
                      { check: /[A-Z]/.test(formData.password), text: language === 'ar' ? 'حرف كبير' : 'Uppercase letter' },
                      { check: /\d/.test(formData.password), text: language === 'ar' ? 'رقم' : 'Number' },
                      { check: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password), text: language === 'ar' ? 'رمز خاص' : 'Special character' }
                    ].map((requirement, index) => (
                      <div key={index} className="flex items-center text-xs">
                        {requirement.check ? (
                          <CheckCircle className="w-3 h-3 text-green-500 mr-4 rtl:mr-0 rtl:ml-4" />
                        ) : (
                          <X className="w-3 h-3 text-slate-400 mr-4 rtl:mr-0 rtl:ml-4" />
                        )}
                        <span className={requirement.check ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}>
                          {requirement.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Password Error */}
              {fieldErrors.password && (
                <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {fieldErrors.password}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                <Lock className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                <span className="flex items-center">
                  {strings.signup.confirmPassword}
                  <span className="text-red-500 ml-1 rtl:ml-0 rtl:mr-1">*</span>
                </span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                    isRTL ? 'pl-12 pr-4' : 'pr-12 pl-4'
                  } ${
                    fieldErrors.confirmPassword 
                      ? 'border-red-500 focus:border-red-500' 
                      : formData.confirmPassword && !fieldErrors.confirmPassword 
                        ? 'border-green-500 focus:border-green-500' 
                        : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                  }`}
                  placeholder={strings.signup.confirmPasswordPlaceholder}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className={`absolute top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-200 ${
                    isRTL ? 'right-3' : 'right-3'
                  }`}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                  {fieldErrors.confirmPassword}
                </div>
              )}
            </div>

            {/* Optional Fields Section - COMMENTED OUT FOR TESTING, WILL BE SHOWN LATER AFTER EXTENSIVE TESTING */}
            {false && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 text-center">
                {language === 'ar' ? 'معلومات إضافية (اختيارية)' : 'Additional Information (Optional)'}
              </h3>
              
              {/* Profile Picture Upload */}
              <div className="mb-6">
                <label className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <Camera className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'صورة الملف الشخصي' : 'Profile Picture'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  {/* Profile Image Preview */}
                  <div className="relative">
                    {profileImagePreview ? (
                      <div className="relative">
                        <img
                          src={profileImagePreview || undefined}
                          alt="Profile preview"
                          className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 dark:border-slate-600"
                        />
                        <button
                          type="button"
                          onClick={removeProfileImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-700 border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center">
                        <Camera className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      type="file"
                      id="profileImage"
                      name="profileImage"
                      accept="image/*"
                      onChange={handleProfileImageChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="profileImage"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                    >
                      <Camera className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                      {language === 'ar' ? 'اختر صورة' : 'Choose Image'}
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {language === 'ar' ? 'PNG, JPG حتى 5 ميجابايت' : 'PNG, JPG up to 5MB'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Birth Date */}
              <div className="mb-6">
                <label htmlFor="birthDate" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <Calendar className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'تاريخ الميلاد' : 'Birth Date'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <input
                  type="date"
                  id="birthDate"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                  className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                    fieldErrors.birthDate 
                      ? 'border-red-500 focus:border-red-500' 
                      : formData.birthDate && !fieldErrors.birthDate 
                        ? 'border-green-500 focus:border-green-500' 
                        : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                  }`}
                />
                {fieldErrors.birthDate && (
                  <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                    {fieldErrors.birthDate}
                  </div>
                )}
              </div>

              {/* Gender */}
              <div className="mb-6">
                <label htmlFor="gender" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <Users className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'الجنس' : 'Gender'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className="w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large"
                >
                  <option value="">{language === 'ar' ? 'اختر الجنس' : 'Select Gender'}</option>
                  <option value="male">{language === 'ar' ? 'ذكر' : 'Male'}</option>
                  <option value="female">{language === 'ar' ? 'أنثى' : 'Female'}</option>
                  <option value="other">{language === 'ar' ? 'آخر' : 'Other'}</option>
                  <option value="prefer-not-to-say">{language === 'ar' ? 'أفضل عدم الإفصاح' : 'Prefer not to say'}</option>
                </select>
              </div>

              {/* Reference Code */}
              {/* <div className="mb-6">
                <label htmlFor="referenceCode" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <Gift className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'كود الإحالة' : 'Reference Code'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <input
                  type="text"
                  id="referenceCode"
                  name="referenceCode"
                  value={formData.referenceCode}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder={language === 'ar' ? 'أدخل كود الإحالة إذا كان لديك' : 'Enter reference code if you have one'}
                  className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                    fieldErrors.referenceCode 
                      ? 'border-red-500 focus:border-red-500' 
                      : formData.referenceCode && !fieldErrors.referenceCode 
                        ? 'border-green-500 focus:border-green-500' 
                        : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                  }`}
                />
                {fieldErrors.referenceCode && (
                  <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                    {fieldErrors.referenceCode}
                  </div>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {language === 'ar' ? 'استخدم كود الإحالة للحصول على مكافآت' : 'Use reference code to get rewards'}
                </p>
              </div> */}

              {/* Title ID */}
              <div className="mb-6">
                <label htmlFor="titleID" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <User className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'المسمى الوظيفي' : 'Professional Title'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <select
                  id="titleID"
                  name="titleID"
                  value={formData.titleID}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className="w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large"
                >
                  {lawyerTitles.map((title) => (
                    <option key={title.id} value={title.id}>
                      {language === 'ar' ? title.nameAr : title.nameEn}
                    </option>
                  ))}
                </select>
              </div>

              {/* Marital Status */}
              <div className="mb-6">
                <label htmlFor="maritalStatus" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <Heart className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'الحالة الاجتماعية' : 'Marital Status'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <select
                  id="maritalStatus"
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className="w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large"
                >
                  <option value="">{language === 'ar' ? 'اختر الحالة الاجتماعية' : 'Select Marital Status'}</option>
                  <option value="single">{language === 'ar' ? 'أعزب/عزباء' : 'Single'}</option>
                  <option value="married">{language === 'ar' ? 'متزوج/متزوجة' : 'Married'}</option>
                  <option value="divorced">{language === 'ar' ? 'مطلق/مطلقة' : 'Divorced'}</option>
                  <option value="widowed">{language === 'ar' ? 'أرمل/أرملة' : 'Widowed'}</option>
                </select>
              </div>

              {/* National ID */}
              <div className="mb-6">
                <label htmlFor="nationalID" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <CreditCard className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'الرقم القومي' : 'National ID'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <input
                  type="text"
                  id="nationalID"
                  name="nationalID"
                  value={formData.nationalID}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder={language === 'ar' ? 'أدخل الرقم القومي' : 'Enter national ID'}
                  className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                    fieldErrors.nationalID 
                      ? 'border-red-500 focus:border-red-500' 
                      : formData.nationalID && !fieldErrors.nationalID 
                        ? 'border-green-500 focus:border-green-500' 
                        : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                  }`}
                />
                {fieldErrors.nationalID && (
                  <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                    {fieldErrors.nationalID}
                  </div>
                )}
              </div>

              {/* Second Phone Number */}
              <div className="mb-6">
                <label htmlFor="phoneNumber2" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <Phone className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'رقم هاتف إضافي' : 'Additional Phone Number'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <input
                  type="tel"
                  id="phoneNumber2"
                  name="phoneNumber2"
                  value={formData.phoneNumber2}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder={language === 'ar' ? 'أدخل رقم هاتف إضافي' : 'Enter additional phone number'}
                  className={`w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large ${
                    fieldErrors.phoneNumber2 
                      ? 'border-red-500 focus:border-red-500' 
                      : formData.phoneNumber2 && !fieldErrors.phoneNumber2 
                        ? 'border-green-500 focus:border-green-500' 
                        : 'border-slate-200 dark:border-slate-600 focus:border-primary'
                  }`}
                />
                {fieldErrors.phoneNumber2 && (
                  <div className="mt-2 flex items-center text-red-600 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />
                    {fieldErrors.phoneNumber2}
                  </div>
                )}
              </div>

              {/* Address */}
              <div className="mb-6">
                <label htmlFor="address" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <MapPin className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'العنوان' : 'Address'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange(e as any)}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder={language === 'ar' ? 'أدخل العنوان الكامل' : 'Enter full address'}
                  rows={3}
                  className="w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large resize-none"
                />
              </div>

              {/* City (Governorate) */}
              <div className="mb-6">
                <label htmlFor="city" className="m3-label-large text-slate-700 dark:text-slate-200 font-medium flex items-center mb-2 text-left rtl:text-right">
                  <MapPin className="w-4 h-4 mr-4 rtl:mr-0 rtl:ml-4 flex-shrink-0" />
                  <span className="flex items-center">
                    {language === 'ar' ? 'المحافظة' : 'Governorate'}
                    <span className="text-slate-400 ml-1 rtl:ml-0 rtl:mr-1 text-sm">({language === 'ar' ? 'اختياري' : 'Optional'})</span>
                  </span>
                </label>
                <select
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className="w-full px-4 py-4 bg-slate-50 dark:bg-slate-700 border-2 border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-slate-600 transition-all duration-300 m3-body-large"
                >
                  <option value="Cairo">{language === 'ar' ? 'القاهرة' : 'Cairo'}</option>
                  <option value="Alexandria">{language === 'ar' ? 'الإسكندرية' : 'Alexandria'}</option>
                  <option value="Giza">{language === 'ar' ? 'الجيزة' : 'Giza'}</option>
                  <option value="Shubra El Kheima">{language === 'ar' ? 'شبرا الخيمة' : 'Shubra El Kheima'}</option>
                  <option value="Port Said">{language === 'ar' ? 'بورسعيد' : 'Port Said'}</option>
                  <option value="Suez">{language === 'ar' ? 'السويس' : 'Suez'}</option>
                  <option value="Luxor">{language === 'ar' ? 'الأقصر' : 'Luxor'}</option>
                  <option value="Aswan">{language === 'ar' ? 'أسوان' : 'Aswan'}</option>
                  <option value="Dakahlia">{language === 'ar' ? 'الدقهلية' : 'Dakahlia'}</option>
                  <option value="Gharbia">{language === 'ar' ? 'الغربية' : 'Gharbia'}</option>
                  <option value="Asyut">{language === 'ar' ? 'أسيوط' : 'Asyut'}</option>
                  <option value="Ismailia">{language === 'ar' ? 'الإسماعيلية' : 'Ismailia'}</option>
                  <option value="Faiyum">{language === 'ar' ? 'الفيوم' : 'Faiyum'}</option>
                  <option value="Sharqia">{language === 'ar' ? 'الشرقية' : 'Sharqia'}</option>
                  <option value="Damietta">{language === 'ar' ? 'دمياط' : 'Damietta'}</option>
                  <option value="Minya">{language === 'ar' ? 'المنيا' : 'Minya'}</option>
                  <option value="Beni Suef">{language === 'ar' ? 'بني سويف' : 'Beni Suef'}</option>
                  <option value="Qena">{language === 'ar' ? 'قنا' : 'Qena'}</option>
                  <option value="Sohag">{language === 'ar' ? 'سوهاج' : 'Sohag'}</option>
                  <option value="Red Sea">{language === 'ar' ? 'البحر الأحمر' : 'Red Sea'}</option>
                  <option value="6th of October">{language === 'ar' ? 'السادس من أكتوبر' : '6th of October'}</option>
                  <option value="South Sinai">{language === 'ar' ? 'جنوب سيناء' : 'South Sinai'}</option>
                  <option value="North Sinai">{language === 'ar' ? 'شمال سيناء' : 'North Sinai'}</option>
                  <option value="Matrouh">{language === 'ar' ? 'مطروح' : 'Matrouh'}</option>
                  <option value="Kafr El Sheikh">{language === 'ar' ? 'كفر الشيخ' : 'Kafr El Sheikh'}</option>
                  <option value="Qalyubia">{language === 'ar' ? 'القليوبية' : 'Qalyubia'}</option>
                  <option value="Beheira">{language === 'ar' ? 'البحيرة' : 'Beheira'}</option>
                  <option value="New Valley">{language === 'ar' ? 'الوادي الجديد' : 'New Valley'}</option>
                </select>
              </div>

            </div>
            )}

            {/* Terms and Conditions */}
            <div className="flex items-start space-x-3 rtl:space-x-reverse">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="w-4 h-4 text-primary bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-primary focus:ring-2"
                />
              </div>
              <div className="text-sm">
                <label htmlFor="terms" className="text-slate-700 dark:text-slate-300 cursor-pointer">
                  {language === 'ar' ? 'أوافق على' : 'I agree to the'}{' '}
                  <Link
                    to="/terms-and-conditions"
                    target="_blank"
                    className="text-accent hover:text-yellow-600 dark:hover:text-yellow-400 font-medium underline transition-colors duration-200"
                  >
                    {language === 'ar' ? 'الشروط والأحكام' : 'Terms and Conditions'}
                  </Link>
                  {' '}{language === 'ar' ? 'و' : 'and'}{' '}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    className="text-accent hover:text-yellow-600 dark:hover:text-yellow-400 font-medium underline transition-colors duration-200"
                  >
                    {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                  </Link>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !acceptedTerms || !isFormValid}
              className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none m3-elevation-2 hover:m3-elevation-3 m3-ripple disabled:bg-slate-400"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="spinner h-5 w-5 border-2 border-white border-t-transparent mr-4 rtl:mr-0 rtl:ml-4"></div>
                  {strings.signup.creatingAccount}
                </div>
              ) : (
                <span className="m3-title-medium">{strings.signup.createAccount}</span>
              )}
            </button>
          </form>

          {/* Sign In Link */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 dark:text-slate-400 m3-body-medium text-center rtl:text-center">
              {strings.signup.haveAccount}{' '}
              <Link
                to="/login"
                className="text-accent hover:text-yellow-600 dark:hover:text-yellow-400 font-semibold transition-colors duration-200"
              >
                {strings.signup.signIn}
              </Link>
            </p>
          </div>

          {/* Copyright */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 dark:text-slate-400 text-sm text-center rtl:text-center">
              {strings.signup.copyright}
            </p>
          </div>
        </div>
      </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SignUp;
